// The page's two tools. The workflow generator writes the setup for a
// repository; the receipt checker runs core/receipt.mjs, the same checks as
// `node core/verify.mjs`, in the browser.
import { provenClaims, verifyReceipt } from "../core/receipt.mjs";
import published from "../app/builds.json" with { type: "json" };
import review from "../review/review.json" with { type: "json" };
import { workflow } from "./workflow.mjs";

const $ = selector => document.querySelector(selector);
const escape = text => String(text).replace(/[&<>"']/g, c => `&#${c.charCodeAt(0)};`);
const code = text => escape(text).replace(/`([^`]+)`/g, "<code>$1</code>");

// ---- Workflow generator ----------------------------------------------------

// The action is pinned to the latest commit on main, looked up when the page loads.
let actionRef = "main";
fetch("https://api.github.com/repos/MultiAgency/private-ai/commits/main", { headers: { accept: "application/vnd.github.sha" } })
  .then(r => (r.ok ? r.text() : Promise.reject()))
  .then(sha => { if (/^[0-9a-f]{40}$/.test(sha)) { actionRef = sha; render(); } })
  .catch(() => {});

const DEFAULT_MODEL = review.defaults.model;
const modelId = model => /^[\w.:-]+\/[\w.:-]+$/.test(model);

function render() {
  const [owner, name] = $("#repo").value.trim().replace(/^https?:\/\/github\.com\//, "").replace(/\.git$/, "").split("/");
  const valid = /^[\w.-]+$/.test(owner ?? "") && /^[\w.-]+$/.test(name ?? "");
  const repo = valid ? `${owner}/${name}` : "OWNER/REPO";
  const branch = $("#branch").value.trim() || "main";
  const model = $("#model").value.trim() || DEFAULT_MODEL;
  const yaml = workflow({ forks: $("#forks").checked, model, unpatched: $("#unpatched").checked, ref: actionRef });
  $("#repo-error").hidden = valid || !$("#repo").value.trim();
  $("#model-error").hidden = modelId(model);

  $("#workflow").textContent = yaml;
  $("#secret-command").textContent = `gh secret set NEARAI_API_KEY -R ${repo}`;
  const secrets = $("#secrets-link");
  secrets.href = `https://github.com/${repo}/settings/secrets/actions/new`;
  const create = $("#create-link");
  create.href = `https://github.com/${repo}/new/${encodeURIComponent(branch)}` +
    `?filename=${encodeURIComponent(".github/workflows/private-review.yml")}&value=${encodeURIComponent(yaml)}`;
  // Until the repository is valid, the links lead nowhere, for the keyboard too.
  for (const link of [secrets, create]) {
    link.toggleAttribute("aria-disabled", !valid);
    if (valid) link.removeAttribute("tabindex");
    else {
      link.removeAttribute("href");
      link.tabIndex = -1;
    }
  }
}

for (const id of ["#repo", "#branch", "#forks", "#model", "#unpatched"]) $(id).addEventListener("input", render);
render();

for (const button of document.querySelectorAll("[data-copy]")) {
  button.addEventListener("click", async () => {
    await navigator.clipboard.writeText($(button.dataset.copy).textContent);
    const label = button.textContent;
    button.textContent = "Copied";
    setTimeout(() => { button.textContent = label; }, 1500);
  });
}

// ---- Theme ----------------------------------------------------------------

$("#theme").addEventListener("click", () => {
  const root = document.documentElement;
  const dark = root.dataset.theme ? root.dataset.theme === "dark" : matchMedia("(prefers-color-scheme: dark)").matches;
  root.dataset.theme = dark ? "light" : "dark";
  try { localStorage.setItem("theme", root.dataset.theme); } catch {}
});

// ---- Receipt checker -------------------------------------------------------

const result = $("#check-result");

/** A receipt's verified claims, or the reason it isn't verified (thrown). */
async function verify(text, options = {}) {
  const { receipt, sha256, gateway, model, runs, subjectConfirmed } = await verifyReceipt(text, { ...options, published });
  const claims = provenClaims({ model: receipt.model, attestation: { model, gateway }, turns: receipt.turns.length, runs });
  return { receipt, sha256, claims, subject: subjectConfirmed ? options.subject : receipt.subject };
}

// A check that doesn't end in "Verified" ends one of two ways, which a reader
// must be able to tell apart: the receipt failed a check (it isn't what was
// signed), or it couldn't be checked at all (no receipt, a broken link, a
// service that didn't answer). Each says so plainly; the exact reason is folded below.
const unreachable = error => error instanceof TypeError || /^(NVIDIA (signing keys|attestation service)|Intel|collateral)|fetch|network|timed? ?out|abort/i.test(error.message);

function unchecked(plain, reason) {
  result.hidden = false;
  result.className = "card result unchecked";
  result.innerHTML = `<p class="verdict">Couldn't check</p><p>${escape(plain)}</p>${reason ? `<details><summary>Details</summary><p>${escape(reason)}</p></details>` : ""}`;
}

function failed(reason) {
  result.hidden = false;
  result.className = "card result failed";
  result.innerHTML = `<p class="verdict">Not verified</p><p>This receipt doesn't match what was signed: something in it was changed, or it wasn't made by the reviewer and enclaves it names.</p><details><summary>What failed</summary><p>${escape(reason)}</p></details>`;
}

async function check(text, label, options) {
  try {
    JSON.parse(text);
  } catch {
    return unchecked(`${label} isn't a receipt: receipts are JSON files, like the run's private-review-receipt.json.`);
  }
  result.hidden = false;
  result.className = "card result running";
  result.innerHTML = `<p>Checking ${escape(label)}: verifying the Intel quotes, NVIDIA's verdict and every signature…</p>`;
  try {
    const { receipt, sha256, claims, subject: named } = await verify(text, options);
    const subject = named
      ? Object.entries(named).map(([k, v]) => `<dt>${escape(k.replace(/_/g, " "))}</dt><dd><code>${escape(v)}</code></dd>`).join("")
      : `<dt>subject</dt><dd>committed, not named: open the link in the review to see which pull request</dd>`;
    result.className = "card result ok";
    result.innerHTML = `
      <p class="verdict">Verified</p>
      <p>Read only inside attested enclaves, and every answer the review used was signed by the model's enclave. Each check, exactly:</p>
      <ul class="checks">
        ${claims.map(([claim, text]) => `<li><strong>${escape(claim)}:</strong> ${code(text)}</li>`).join("")}
      </ul>
      <dl>${subject}<dt>model</dt><dd><code>${escape(receipt.model)}</code></dd><dt>created</dt><dd>${escape(receipt.created_at)}</dd><dt>receipt sha256</dt><dd><code>${escape(sha256)}</code></dd></dl>`;
  } catch (error) {
    if (unreachable(error)) unchecked("A service the check relies on (Intel, NVIDIA or OutLayer) didn't answer. Try again in a minute.", error.message);
    else if (/^unknown receipt version/.test(error.message)) unchecked(`${label} isn't a receipt this checker knows.`, error.message);
    else failed(error.message);
  }
}

// The hero's seal: the same checks on the sample review's receipt. Its lines
// arrive one by one, the page's one piece of motion, in answer to the press.
const seal = $("#seal");
seal.addEventListener("click", async () => {
  const list = $("#seal-checks");
  const note = $("#seal-note");
  seal.disabled = true;
  seal.classList.remove("ok", "failed");
  seal.classList.add("checking");
  seal.querySelector("span").textContent = "Checking";
  note.textContent = "Fetching Intel's collateral and NVIDIA's keys, then checking every signature…";
  list.hidden = true;
  const started = performance.now();
  try {
    const { claims } = await verify(await fetch("sample-receipt.json").then(r => r.text()));
    list.innerHTML = claims.map(([claim, text], i) => `<li style="--i:${i}"><strong>${escape(claim)}</strong> ${code(text)}</li>`).join("");
    list.hidden = false;
    seal.classList.add("ok");
    seal.querySelector("span").textContent = "Sealed";
    note.textContent = `Verified in ${((performance.now() - started) / 1000).toFixed(1)} s, here in your browser.`;
  } catch (error) {
    seal.classList.add("failed");
    seal.querySelector("span").textContent = unreachable(error) ? "Couldn't check" : "Not verified";
    note.textContent = unreachable(error) ? "A service the check relies on didn't answer. Try again in a minute, or use the checker below." : error.message;
  } finally {
    seal.classList.remove("checking");
    seal.disabled = false;
  }
});

// A hosted review links its receipt as #check&receipt=…&project=…&salt=…&subject=…:
// the receipt is fetched from OutLayer's public storage, and the salt and
// subject (in the fragment, which never reaches a server) open its commitment.
async function checkLinked() {
  if (!location.hash.startsWith("#check&")) return;
  const params = new URLSearchParams(location.hash.replace(/^#check&?/, ""));
  const [sha, project] = [params.get("receipt"), params.get("project")];
  if (!sha || !project) return;
  document.getElementById("check")?.scrollIntoView();
  let subject;
  try {
    subject = params.get("subject") && JSON.parse(atob(params.get("subject").replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return unchecked("This link is incomplete: copy the whole link from the review and open it again.");
  }
  let response;
  try {
    response = await fetch(`https://api.outlayer.ai/public/storage/get?project=${encodeURIComponent(project)}&key=${encodeURIComponent(`receipt:${sha}`)}&format=raw`);
  } catch (error) {
    return unchecked("OutLayer, where the receipt is published, didn't answer. Try again in a minute.", error.message);
  }
  if (!response.ok) return unchecked("No receipt is published under this link: check that it was copied whole.", `${project}, receipt ${sha}: ${response.status}`);
  await check(await response.text(), "the receipt this review links", { salt: params.get("salt") ?? undefined, subject: subject || undefined, expectSha256: sha });
}
checkLinked();
// A review's link opened in a tab already on this page.
addEventListener("hashchange", checkLinked);

// GitHub sends people here after they install the App, or change which
// repositories it can see: to its Setup URL (…/#installed), with
// installation_id and setup_action added after it, before or after the "#".
if (/^#installed\b/.test(location.hash) || /[?&#]setup_action=(install|update)\b/.test(location.href)) {
  const banner = $("#installed-banner");
  banner.hidden = false;
  banner.scrollIntoView();
}

$("#check-sample").addEventListener("click", async () => {
  const response = await fetch("sample-receipt.json");
  check(await response.text(), "the sample receipt");
});

const drop = $("#drop");
const readFile = file => file && file.text().then(text => check(text, file.name));
$("#receipt-file").addEventListener("change", event => readFile(event.target.files[0]));
drop.addEventListener("dragover", event => { event.preventDefault(); drop.classList.add("over"); });
drop.addEventListener("dragleave", () => drop.classList.remove("over"));
drop.addEventListener("drop", event => {
  event.preventDefault();
  drop.classList.remove("over");
  readFile(event.dataTransfer.files[0]);
});
